// Labels, filters and time helpers for event speakers and the line-up, shared by the business
// wizard and the public event page. Kept free of runtime imports so the node:test suite can load it.
import type {EventLineupDay, EventSpeaker, LineupSlotKind, SpeakerRole} from "../types/event";

export const SPEAKER_ROLES: Array<{ value: SpeakerRole; label: string }> = [
    {value: "SPEAKER", label: "Speaker"},
    {value: "KEYNOTE_SPEAKER", label: "Keynote speaker"},
    {value: "PRESENTER", label: "Presenter"},
    {value: "PANELIST", label: "Panellist"},
    {value: "PANEL_MODERATOR", label: "Panel moderator"},
    {value: "MODERATOR", label: "Moderator"},
    {value: "HOST", label: "Host"},
    {value: "FACILITATOR", label: "Facilitator"},
    {value: "PERFORMER", label: "Performer"},
];

export const SLOT_KINDS: Array<{ value: LineupSlotKind; label: string }> = [
    {value: "TALK", label: "Talk"},
    {value: "PANEL", label: "Panel"},
    {value: "WORKSHOP", label: "Workshop"},
    {value: "PERFORMANCE", label: "Performance"},
    {value: "BREAK", label: "Break"},
    {value: "OTHER", label: "Other"},
];

export const PROGRAMME_TIME_ZONE = "Africa/Windhoek";

/** "KEYNOTE_SPEAKER" becomes "Keynote Speaker"; the fallback for values this client does not know yet. */
export function titleCase(raw: string): string {
    return raw
        .toLowerCase()
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

export function roleLabel(role: SpeakerRole): string {
    return SPEAKER_ROLES.find((r) => r.value === role)?.label ?? titleCase(String(role ?? ""));
}

export function kindLabel(kind: LineupSlotKind): string {
    return SLOT_KINDS.find((k) => k.value === kind)?.label ?? titleCase(String(kind ?? ""));
}

/** A speaker's roles in the order given, as on their card: "Presenter & Panel moderator". */
export function joinRoles(roles: SpeakerRole[] | null | undefined): string {
    return (roles ?? []).map(roleLabel).join(" & ");
}

/** Every role held by at least one speaker, known roles in the standard order, unknown ones after. */
export function distinctRoles(speakers: EventSpeaker[]): SpeakerRole[] {
    const held = new Set<SpeakerRole>();
    speakers.forEach((s) => (s.roles ?? []).forEach((r) => held.add(r)));
    const known = SPEAKER_ROLES.map((r) => r.value).filter((r) => held.has(r));
    const unknown = [...held].filter((r) => !SPEAKER_ROLES.some((k) => k.value === r));
    return [...known, ...unknown];
}

export function filterSpeakersByRole(speakers: EventSpeaker[], role: SpeakerRole | null): EventSpeaker[] {
    if (!role) return speakers;
    return speakers.filter((s) => (s.roles ?? []).includes(role));
}

/** Distinct, non-blank stage names across the line-up, in order of first appearance. */
export function distinctStages(days: EventLineupDay[]): string[] {
    const seen: string[] = [];
    days.forEach((d) => (d.slots ?? []).forEach((s) => {
        const stage = s.stage?.trim();
        if (stage && !seen.includes(stage)) seen.push(stage);
    }));
    return seen;
}

/**
 * The line-up narrowed to one stage. Slots without a stage (a lunch break for everyone, say) stay
 * under every stage, and days left with no slots are dropped.
 */
export function filterLineupByStage(days: EventLineupDay[], stage: string | null): EventLineupDay[] {
    if (!stage) return days;
    return days
        .map((d) => ({
            ...d,
            slots: (d.slots ?? []).filter((s) => !s.stage?.trim() || s.stage.trim() === stage),
        }))
        .filter((d) => d.slots.length > 0);
}

/** The programme from a details response; missing fields (older backends) and empty days become empty. */
export function programmeOf(details: { speakers?: EventSpeaker[] | null; lineup?: EventLineupDay[] | null }) {
    return {
        speakers: details.speakers ?? [],
        lineup: (details.lineup ?? []).filter((d) => (d.slots ?? []).length > 0),
    };
}

function windhoekParts(iso: string): Record<string, string> | null {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: PROGRAMME_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).formatToParts(d);
    const out: Record<string, string> = {};
    parts.forEach((p) => (out[p.type] = p.value));
    // some engines write midnight as 24 when hour12 is off
    if (out.hour === "24") out.hour = "00";
    return out;
}

/** "14:30" in Windhoek time, or "" for a missing or broken value. */
export function formatSlotTime(iso: string | null | undefined): string {
    if (!iso) return "";
    const p = windhoekParts(iso);
    return p ? `${p.hour}:${p.minute}` : "";
}

/** "14:30 to 15:15", or just the start when there is no end. */
export function formatSlotRange(startsAt: string, endsAt?: string | null): string {
    const start = formatSlotTime(startsAt);
    const end = formatSlotTime(endsAt);
    return end ? `${start} to ${end}` : start;
}

/** A UTC instant as a datetime-local value in Windhoek wall time ("2026-12-01T10:00"). */
export function isoToWindhoekInput(iso: string | null | undefined): string {
    if (!iso) return "";
    const p = windhoekParts(iso);
    return p ? `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}` : "";
}

/** "yyyy-MM-dd" as a long en-GB date, e.g. "Tuesday, 1 December 2026". */
export function formatProgrammeDate(date: string): string {
    const d = new Date(`${date}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return date;
    return new Intl.DateTimeFormat("en-GB", {
        timeZone: "UTC",
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(d);
}

/** The heading for a line-up day: its event day name, or its date when the slots have no event day. */
export function lineupDayLabel(day: EventLineupDay): string {
    return day.name?.trim() || formatProgrammeDate(day.date);
}
